import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import CreateEventButton from '../components/CreateEventButton';
import {
  DollarSign, TrendingUp, Users, Target, Phone, Mail, Calendar,
  Plus, Clock, CheckCircle2, XCircle,
  MessageSquare, Wallet, Award, BarChart3, FileText, Send, ChevronRight,
  Briefcase, Zap, Bot, Sparkles, ArrowRight, Loader2, ExternalLink,
  ChevronLeft, Trash2, Edit, MapPin, Link2, Repeat, Play, Pause, SkipForward, Eye,
  GraduationCap, Lock, Shield, Rocket, CreditCard, Building2, BookOpen, PenTool, Layers,
  ArrowLeft, Globe, Search
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAD_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
  { value: 'proposal_viewed', label: 'Viewed', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  { value: 'negotiating', label: 'Negotiating', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { value: 'won', label: 'Won', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { value: 'lost', label: 'Lost', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

const OUTREACH_TYPES = [
  { value: 'call', label: 'Phone Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'meeting', label: 'Meeting', icon: Calendar },
  { value: 'note', label: 'Note', icon: MessageSquare },
];

const EVENT_TYPES = [
  { value: 'meeting', label: 'Meeting', color: '#6366f1' },
  { value: 'call', label: 'Call', color: '#3b82f6' },
  { value: 'follow_up', label: 'Follow-up', color: '#f59e0b' },
  { value: 'demo', label: 'Demo', color: '#10b981' },
];

// ============= AI COPILOT COMPONENT =============
function AICopilot({ token, leads }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const messagesEndRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/sales/ai/copilot`, {
        message: userMsg,
        session_id: sessionId,
        lead_id: selectedLeadId && selectedLeadId !== 'none' ? selectedLeadId : null
      }, { headers });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      toast.error('AI response failed');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const newSession = () => {
    setMessages([]);
    setSessionId(null);
    setSelectedLeadId('');
  };

  return (
    <div className="flex flex-col h-[600px]" data-testid="ai-copilot">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-indigo-400" />
          <h3 className="text-sm font-semibold text-foreground">AI Sales Copilot</h3>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
            <SelectTrigger className="w-[180px] h-8 text-xs bg-muted border-border" data-testid="copilot-lead-select">
              <SelectValue placeholder="Link to lead..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No lead context</SelectItem>
              {leads.map(l => (
                <SelectItem key={l.lead_id} value={l.lead_id}>{l.client_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={newSession} data-testid="copilot-new-session">
            <Plus className="h-3 w-3 mr-1" /> New Chat
          </Button>
        </div>
      </div>

      <Card className="bg-muted/60 border-border flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="copilot-messages">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Sparkles className="h-10 w-10 text-indigo-500/40 mb-3" />
              <p className="text-muted-foreground text-sm">Ask me anything about your deals</p>
              <p className="text-muted-foreground text-xs mt-1">I can help with pitches, objection handling, strategy, and more</p>
              <div className="flex flex-wrap gap-2 mt-4 justify-center">
                {['How do I handle budget objections?', 'Help me close a wedding lead', 'What should I follow up on today?'].map(q => (
                  <button key={q} onClick={() => { setInput(q); }} className="text-xs px-3 py-1.5 rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 hover:bg-indigo-500/20 transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                m.role === 'user'
                  ? 'bg-indigo-600 text-foreground'
                  : 'bg-muted text-foreground'
              }`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-xl px-4 py-2.5">
                <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className="p-3 border-t border-border">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Ask your sales copilot..."
              className="bg-muted border-border text-sm"
              data-testid="copilot-input"
            />
            <Button onClick={sendMessage} disabled={loading || !input.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="copilot-send">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}


// ============= EMAIL WORKSPACE COMPONENT =============
function EmailWorkspace({ token, leads }) {
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftType, setDraftType] = useState('initial');
  const headers = { Authorization: `Bearer ${token}` };

  const selectedLead = leads.find(l => l.lead_id === selectedLeadId);

  const handleAIDraft = async () => {
    if (!selectedLeadId || selectedLeadId === 'none') { toast.error('Select a lead first'); return; }
    setDrafting(true);
    try {
      const res = await axios.post(`${API}/sales/ai/draft-email`, {
        lead_id: selectedLeadId,
        pitch_type: draftType
      }, { headers });
      setSubject(res.data.subject || '');
      setBody(res.data.body || '');
      toast.success('AI draft generated!');
    } catch (err) {
      toast.error('Failed to generate draft');
    } finally {
      setDrafting(false);
    }
  };

  const handleSend = async () => {
    if (!selectedLeadId || selectedLeadId === 'none' || !subject || !body) { toast.error('Fill all fields'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/sales/email/send`, {
        lead_id: selectedLeadId,
        subject,
        body
      }, { headers });
      toast.success('Email sent!');
      setSubject('');
      setBody('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="email-workspace">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-3">
          <Card className="bg-muted/60 border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-foreground flex items-center gap-2">
                <Mail className="h-4 w-4 text-emerald-400" /> Compose Email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">To (Select Lead)</Label>
                <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
                  <SelectTrigger className="bg-muted border-border h-9" data-testid="email-lead-select">
                    <SelectValue placeholder="Select a lead..." />
                  </SelectTrigger>
                  <SelectContent>
                    {leads.map(l => (
                      <SelectItem key={l.lead_id} value={l.lead_id}>
                        {l.client_name} ({l.client_email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Subject</Label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} className="bg-muted border-border h-9" placeholder="Email subject..." data-testid="email-subject" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Body</Label>
                <Textarea value={body} onChange={e => setBody(e.target.value)} className="bg-muted border-border min-h-[200px]" placeholder="Write your email..." data-testid="email-body" />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Select value={draftType} onValueChange={setDraftType}>
                    <SelectTrigger className="w-[140px] h-8 text-xs bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="initial">Initial Outreach</SelectItem>
                      <SelectItem value="follow_up">Follow-up</SelectItem>
                      <SelectItem value="closing">Closing</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={handleAIDraft} disabled={drafting || !selectedLeadId || selectedLeadId === 'none'} className="h-8 text-xs" data-testid="ai-draft-btn">
                    {drafting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1 text-amber-400" />}
                    AI Draft
                  </Button>
                </div>
                <Button onClick={handleSend} disabled={sending || !selectedLeadId || selectedLeadId === 'none' || !subject || !body} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-email-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Send Email
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar: Selected lead info */}
        <div>
          {selectedLead ? (
            <Card className="bg-muted/60 border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-foreground">Recipient</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                <p className="text-foreground font-medium">{selectedLead.client_name}</p>
                <p className="text-muted-foreground">{selectedLead.client_email}</p>
                <p className="text-muted-foreground">{selectedLead.event_type} | {selectedLead.guest_count} guests</p>
                <p className="text-emerald-400 font-medium">${(selectedLead.budget || 0).toLocaleString()}</p>
                <Badge className={LEAD_STATUSES.find(s => s.value === selectedLead.status)?.color || ''}>
                  {LEAD_STATUSES.find(s => s.value === selectedLead.status)?.label || selectedLead.status}
                </Badge>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-muted/60 border-border">
              <CardContent className="py-12 text-center">
                <Mail className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground text-xs">Select a lead to compose email</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}


// ============= CALENDAR COMPONENT =============
function SalesCalendar({ token, leads }) {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [gcalStatus, setGcalStatus] = useState({ connected: false });
  const [syncing, setSyncing] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '', start: '', end: '', lead_id: '', type: 'meeting', notes: '', location: ''
  });
  const headers = { Authorization: `Bearer ${token}` };

  const fetchEvents = useCallback(async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).toISOString();
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59).toISOString();
      const res = await axios.get(`${API}/sales/calendar/events?start=${startOfMonth}&end=${endOfMonth}`, { headers });
      setEvents(res.data);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  }, [token, currentDate]);

  const fetchGcalStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/oauth/calendar/status`, { headers });
      setGcalStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch gcal status:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchEvents();
    fetchGcalStatus();
  }, [fetchEvents, fetchGcalStatus]);

  // Check for gcal callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gcal') === 'success') {
      toast.success('Google Calendar connected!');
      fetchGcalStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('gcal') === 'error') {
      toast.error(`Google Calendar error: ${params.get('reason') || 'unknown'}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const connectGoogleCalendar = async () => {
    try {
      const res = await axios.get(`${API}/oauth/calendar/connect`, { headers });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error('Failed to connect Google Calendar');
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await axios.delete(`${API}/oauth/calendar/disconnect`, { headers });
      setGcalStatus({ connected: false });
      toast.success('Google Calendar disconnected');
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const syncFromGoogle = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/sales/calendar/sync-from-google`, {}, { headers });
      toast.success(res.data.message);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.start || !newEvent.end) {
      toast.error('Title, start, and end are required');
      return;
    }
    try {
      const payload = {
        ...newEvent,
        start: new Date(newEvent.start).toISOString(),
        end: new Date(newEvent.end).toISOString(),
        lead_id: newEvent.lead_id && newEvent.lead_id !== 'none' ? newEvent.lead_id : null
      };
      await axios.post(`${API}/sales/calendar/events`, payload, { headers });
      toast.success('Event created!');
      setShowNewEvent(false);
      setNewEvent({ title: '', start: '', end: '', lead_id: '', type: 'meeting', notes: '', location: '' });
      fetchEvents();
    } catch (err) {
      toast.error('Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/sales/calendar/events/${eventId}`, { headers });
      toast.success('Event deleted');
      fetchEvents();
    } catch (err) {
      toast.error('Failed to delete event');
    }
  };

  // Calendar grid helpers
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.start && e.start.startsWith(dateStr));
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div data-testid="sales-calendar">
      {/* Google Calendar Connection */}
      <Card className="bg-muted/60 border-border mb-4">
        <CardContent className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${gcalStatus.connected ? 'bg-emerald-400' : 'bg-accent'}`} />
            <span className="text-xs text-muted-foreground">
              {gcalStatus.connected
                ? `Google Calendar connected (${gcalStatus.google_email})`
                : 'Google Calendar not connected'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {gcalStatus.connected ? (
              <>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={syncFromGoogle} disabled={syncing} data-testid="sync-google-btn">
                  {syncing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ArrowRight className="h-3 w-3 mr-1" />}
                  Sync from Google
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-400 hover:text-red-300" onClick={disconnectGoogleCalendar} data-testid="disconnect-google-btn">
                  Disconnect
                </Button>
              </>
            ) : (
              <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={connectGoogleCalendar} data-testid="connect-google-btn">
                <Link2 className="h-3 w-3 mr-1" /> Connect Google Calendar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={prevMonth} className="h-8 w-8 p-0" data-testid="cal-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="text-lg font-semibold text-foreground" data-testid="cal-month-title">
            {monthNames[month]} {year}
          </h3>
          <Button variant="ghost" size="sm" onClick={nextMonth} className="h-8 w-8 p-0" data-testid="cal-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" onClick={() => setShowNewEvent(true)} data-testid="new-event-btn">
          <Plus className="h-3 w-3 mr-1" /> New Event
        </Button>
      </div>

      {/* Calendar Grid */}
      <Card className="bg-muted/60 border-border">
        <CardContent className="p-0">
          <div className="grid grid-cols-7">
            {dayNames.map(d => (
              <div key={d} className="p-2 text-center text-[10px] font-semibold text-muted-foreground border-b border-border">
                {d}
              </div>
            ))}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[80px] border-b border-r border-border/50 bg-background/30" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayEvents = getEventsForDay(day);
              const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
              return (
                <div
                  key={day}
                  className={`min-h-[80px] border-b border-r border-border/50 p-1 hover:bg-muted/20 transition-colors cursor-pointer ${isToday ? 'bg-indigo-500/5' : ''}`}
                  onClick={() => {
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    setNewEvent(prev => ({ ...prev, start: `${dateStr}T09:00`, end: `${dateStr}T10:00` }));
                    setShowNewEvent(true);
                  }}
                  data-testid={`cal-day-${day}`}
                >
                  <span className={`text-xs font-medium inline-flex items-center justify-center w-6 h-6 rounded-full ${isToday ? 'bg-indigo-600 text-foreground' : 'text-muted-foreground'}`}>
                    {day}
                  </span>
                  <div className="space-y-0.5 mt-0.5">
                    {dayEvents.slice(0, 3).map(ev => {
                      const evType = EVENT_TYPES.find(t => t.value === ev.type) || EVENT_TYPES[0];
                      return (
                        <div
                          key={ev.event_id}
                          className="text-[9px] px-1 py-0.5 rounded truncate"
                          style={{ backgroundColor: evType.color + '22', color: evType.color, borderLeft: `2px solid ${evType.color}` }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingEvent(ev);
                          }}
                          title={ev.title}
                        >
                          {ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className="text-[9px] text-muted-foreground px-1">+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Events List */}
      <Card className="bg-muted/60 border-border mt-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-foreground">Upcoming Events</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2" data-testid="upcoming-events">
            {events.filter(e => new Date(e.start) >= new Date()).slice(0, 8).map(ev => {
              const evType = EVENT_TYPES.find(t => t.value === ev.type) || EVENT_TYPES[0];
              const linkedLead = leads.find(l => l.lead_id === ev.lead_id);
              return (
                <div key={ev.event_id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 rounded-full" style={{ backgroundColor: evType.color }} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{ev.title}</p>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {new Date(ev.start).toLocaleDateString()} {new Date(ev.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {ev.location && <><MapPin className="h-3 w-3 ml-1" /> {ev.location}</>}
                        {linkedLead && <><Users className="h-3 w-3 ml-1" /> {linkedLead.client_name}</>}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                    onClick={() => handleDeleteEvent(ev.event_id)}
                    data-testid={`delete-event-${ev.event_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
            {events.filter(e => new Date(e.start) >= new Date()).length === 0 && (
              <p className="text-center text-muted-foreground py-6 text-xs">No upcoming events</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* New Event Dialog */}
      <Dialog open={showNewEvent} onOpenChange={setShowNewEvent}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="new-event-dialog">
          <DialogHeader><DialogTitle className="text-foreground">New Calendar Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input value={newEvent.title} onChange={e => setNewEvent(p => ({ ...p, title: e.target.value }))} className="bg-muted border-border h-9" data-testid="event-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Start *</Label>
                <Input type="datetime-local" value={newEvent.start} onChange={e => setNewEvent(p => ({ ...p, start: e.target.value }))} className="bg-muted border-border h-9" data-testid="event-start-input" />
              </div>
              <div>
                <Label className="text-xs">End *</Label>
                <Input type="datetime-local" value={newEvent.end} onChange={e => setNewEvent(p => ({ ...p, end: e.target.value }))} className="bg-muted border-border h-9" data-testid="event-end-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={newEvent.type} onValueChange={v => setNewEvent(p => ({ ...p, type: v }))}>
                  <SelectTrigger className="bg-muted border-border h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Link to Lead</Label>
                <Select value={newEvent.lead_id} onValueChange={v => setNewEvent(p => ({ ...p, lead_id: v }))}>
                  <SelectTrigger className="bg-muted border-border h-9"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {leads.map(l => (<SelectItem key={l.lead_id} value={l.lead_id}>{l.client_name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Location</Label>
              <Input value={newEvent.location} onChange={e => setNewEvent(p => ({ ...p, location: e.target.value }))} className="bg-muted border-border h-9" placeholder="Office, Zoom, etc." />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={newEvent.notes} onChange={e => setNewEvent(p => ({ ...p, notes: e.target.value }))} className="bg-muted border-border" rows={2} />
            </div>
            <Button onClick={handleCreateEvent} className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={!newEvent.title || !newEvent.start || !newEvent.end} data-testid="create-event-submit">
              <Plus className="h-4 w-4 mr-1" /> Create Event
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Detail Dialog */}
      <Dialog open={!!editingEvent} onOpenChange={() => setEditingEvent(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="event-detail-dialog">
          {editingEvent && (
            <>
              <DialogHeader><DialogTitle className="text-foreground">{editingEvent.title}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{new Date(editingEvent.start).toLocaleString()} - {new Date(editingEvent.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                {editingEvent.location && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" /> {editingEvent.location}
                  </div>
                )}
                {editingEvent.notes && <p className="text-muted-foreground text-xs p-2 bg-muted/50 rounded">{editingEvent.notes}</p>}
                {editingEvent.google_event_id && (
                  <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">
                    <ExternalLink className="h-2.5 w-2.5 mr-1" /> Synced with Google
                  </Badge>
                )}
                <Button variant="destructive" size="sm" className="w-full mt-2" onClick={() => { handleDeleteEvent(editingEvent.event_id); setEditingEvent(null); }}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Event
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============= LEAD DETAIL WITH AI TOOLS =============
function LeadDetailPanel({ lead, token, onBack, onStatusChange, onLogOutreach, outreachHistory }) {
  const [pitchLoading, setPitchLoading] = useState(false);
  const [pitch, setPitch] = useState(null);
  const [pitchType, setPitchType] = useState('initial');
  const [followUp, setFollowUp] = useState(null);
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const statusDef = LEAD_STATUSES.find(s => s.value === lead.status) || LEAD_STATUSES[0];

  const generatePitch = async () => {
    setPitchLoading(true);
    try {
      const res = await axios.post(`${API}/sales/ai/pitch`, { lead_id: lead.lead_id, pitch_type: pitchType }, { headers });
      setPitch(res.data.pitch);
    } catch (err) {
      toast.error('Failed to generate pitch');
    } finally {
      setPitchLoading(false);
    }
  };

  const getFollowUpSuggestions = async () => {
    setFollowUpLoading(true);
    try {
      const res = await axios.get(`${API}/sales/ai/follow-up/${lead.lead_id}`, { headers });
      setFollowUp(res.data);
    } catch (err) {
      toast.error('Failed to get suggestions');
    } finally {
      setFollowUpLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="lead-detail-panel">
      <Button variant="ghost" size="sm" onClick={onBack} className="text-xs text-muted-foreground mb-2" data-testid="back-to-leads">
        <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to leads
      </Button>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Lead Info + Timeline */}
        <div className="md:col-span-2 space-y-4">
          <Card className="bg-muted/60 border-border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-foreground">{lead.client_name}</CardTitle>
                <Badge className={statusDef.color}>{statusDef.label}</Badge>
              </div>
            </CardHeader>
            <CardContent data-testid="lead-detail-card">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Email:</span> <span className="text-foreground ml-1">{lead.client_email}</span></div>
                <div><span className="text-muted-foreground">Phone:</span> <span className="text-foreground ml-1">{lead.client_phone || '-'}</span></div>
                <div><span className="text-muted-foreground">Event:</span> <span className="text-foreground ml-1">{lead.event_type}</span></div>
                <div><span className="text-muted-foreground">Guests:</span> <span className="text-foreground ml-1">{lead.guest_count}</span></div>
                <div><span className="text-muted-foreground">Budget:</span> <span className="text-emerald-400 ml-1 font-medium">${(lead.budget || 0).toLocaleString()}</span></div>
                <div><span className="text-muted-foreground">Score:</span> <span className={`ml-1 font-medium ${lead.ai_grade === 'HOT' ? 'text-red-400' : lead.ai_grade === 'WARM' ? 'text-amber-400' : 'text-blue-400'}`}>{lead.ai_score ? `${lead.ai_score}/100 (${lead.ai_grade})` : 'Not scored'}</span></div>
              </div>
              {lead.ai_reason && <p className="text-xs text-muted-foreground mt-2 italic">{lead.ai_reason}</p>}
              {lead.notes && <p className="text-sm text-muted-foreground mt-3 p-3 bg-muted/50 rounded-lg">{lead.notes}</p>}
              <div className="flex gap-2 mt-4">
                <Select value={lead.status} onValueChange={(v) => onStatusChange(lead.lead_id, v, lead.budget)}>
                  <SelectTrigger className="w-[150px] h-9 text-xs bg-muted border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAD_STATUSES.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                </Select>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={onLogOutreach}>
                  <Plus className="h-3 w-3 mr-1" /> Log Activity
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* AI Pitch Generator */}
          <Card className="bg-muted/60 border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-foreground flex items-center gap-2">
                <FileText className="h-4 w-4 text-amber-400" /> AI Pitch Generator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-3">
                <Select value={pitchType} onValueChange={setPitchType}>
                  <SelectTrigger className="w-[160px] h-8 text-xs bg-muted border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="initial">Initial Pitch</SelectItem>
                    <SelectItem value="follow_up">Follow-up</SelectItem>
                    <SelectItem value="closing">Closing</SelectItem>
                    <SelectItem value="objection_handling">Objection Handling</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={generatePitch} disabled={pitchLoading} className="h-8 text-xs bg-amber-600 hover:bg-amber-700" data-testid="generate-pitch-btn">
                  {pitchLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                  Generate Pitch
                </Button>
              </div>
              {pitch && (
                <div className="p-3 bg-muted/50 rounded-lg text-sm text-foreground whitespace-pre-wrap max-h-[300px] overflow-y-auto" data-testid="pitch-output">
                  {pitch}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card className="bg-muted/60 border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-foreground">Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent data-testid="activity-timeline">
              <div className="space-y-3">
                {outreachHistory.map(o => {
                  const TypeIcon = OUTREACH_TYPES.find(t => t.value === o.type)?.icon || MessageSquare;
                  return (
                    <div key={o.outreach_id} className="flex gap-3 p-3 bg-muted/40 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                        <TypeIcon className="h-4 w-4 text-indigo-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground capitalize">{o.type}</p>
                          {o.outcome && (
                            <Badge variant="outline" className={`text-[10px] ${o.outcome === 'positive' ? 'text-emerald-400 border-emerald-500/30' : o.outcome === 'negative' ? 'text-red-400 border-red-500/30' : 'text-muted-foreground'}`}>
                              {o.outcome}
                            </Badge>
                          )}
                          {o.duration_minutes && <span className="text-[10px] text-muted-foreground">{o.duration_minutes}min</span>}
                        </div>
                        {o.subject && <p className="text-xs text-muted-foreground mt-0.5">{o.subject}</p>}
                        {o.notes && <p className="text-xs text-muted-foreground mt-1">{o.notes}</p>}
                        <p className="text-[10px] text-muted-foreground mt-1">{new Date(o.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  );
                })}
                {outreachHistory.length === 0 && (
                  <p className="text-center text-muted-foreground py-6 text-sm">No activity logged yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar: AI Follow-Up + Quick Actions */}
        <div className="space-y-4">
          <Card className="bg-muted/60 border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-foreground flex items-center gap-2"><Zap className="h-4 w-4 text-amber-400" /> AI Follow-Up</CardTitle></CardHeader>
            <CardContent>
              <Button size="sm" variant="outline" className="w-full text-xs mb-3" onClick={getFollowUpSuggestions} disabled={followUpLoading} data-testid="follow-up-btn">
                {followUpLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1 text-amber-400" />}
                Get AI Suggestions
              </Button>
              {followUp && (
                <div className="space-y-2" data-testid="follow-up-output">
                  <Badge className={followUp.priority === 'high' ? 'bg-red-500/20 text-red-400' : followUp.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}>
                    {followUp.priority} priority
                  </Badge>
                  <p className="text-xs text-muted-foreground italic">{followUp.overall_strategy}</p>
                  <div className="space-y-1.5">
                    {(followUp.suggested_actions || []).map((a, i) => (
                      <div key={i} className="p-2 rounded bg-muted/50 text-xs">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="text-[9px] capitalize">{a.type}</Badge>
                          <span className="text-muted-foreground">{a.timing}</span>
                        </div>
                        <p className="text-muted-foreground mt-1">{a.message_preview}</p>
                        <p className="text-muted-foreground text-[10px] mt-0.5">{a.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-muted/60 border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-foreground">Quick Actions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {[{ type: 'call', icon: Phone, color: 'text-blue-400', label: 'Log Call' },
                { type: 'email', icon: Mail, color: 'text-emerald-400', label: 'Log Email' },
                { type: 'meeting', icon: Calendar, color: 'text-purple-400', label: 'Log Meeting' }
              ].map(a => (
                <Button key={a.type} variant="outline" className="w-full justify-start text-xs h-9" onClick={() => onLogOutreach(a.type)}>
                  <a.icon className={`h-3.5 w-3.5 mr-2 ${a.color}`} /> {a.label}
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}


// ============= SEQUENCES COMPONENT =============
function SequenceManager({ token, leads }) {
  const [sequences, setSequences] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedSeq, setSelectedSeq] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newSeq, setNewSeq] = useState({ lead_id: '', mode: 'approval', custom_goal: '' });
  const [editingStep, setEditingStep] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [seqRes, statsRes] = await Promise.all([
        axios.get(`${API}/sales/sequences`, { headers }),
        axios.get(`${API}/sales/sequences/stats/overview`, { headers })
      ]);
      setSequences(seqRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch sequences:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!newSeq.lead_id) { toast.error('Select a lead'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/sales/sequences`, {
        lead_id: newSeq.lead_id,
        mode: newSeq.mode,
        custom_goal: newSeq.custom_goal || null
      }, { headers });
      toast.success(`Sequence created with ${res.data.total_steps} AI-generated steps!`);
      setShowCreate(false);
      setNewSeq({ lead_id: '', mode: 'approval', custom_goal: '' });
      fetchData();
      setSelectedSeq(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create sequence');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (seqId, newStatus) => {
    try {
      await axios.put(`${API}/sales/sequences/${seqId}`, { status: newStatus }, { headers });
      toast.success(`Sequence ${newStatus}`);
      fetchData();
      if (selectedSeq?.sequence_id === seqId) {
        const res = await axios.get(`${API}/sales/sequences/${seqId}`, { headers });
        setSelectedSeq(res.data);
      }
    } catch (err) {
      toast.error('Failed to update sequence');
    }
  };

  const handleApproveStep = async (seqId, stepId) => {
    try {
      await axios.post(`${API}/sales/sequences/${seqId}/steps/${stepId}/approve`, {}, { headers });
      toast.success('Email sent!');
      fetchData();
      const res = await axios.get(`${API}/sales/sequences/${seqId}`, { headers });
      setSelectedSeq(res.data);
    } catch (err) {
      toast.error('Failed to approve step');
    }
  };

  const handleSkipStep = async (seqId, stepId) => {
    try {
      await axios.post(`${API}/sales/sequences/${seqId}/steps/${stepId}/skip`, {}, { headers });
      toast.success('Step skipped');
      fetchData();
      const res = await axios.get(`${API}/sales/sequences/${seqId}`, { headers });
      setSelectedSeq(res.data);
    } catch (err) {
      toast.error('Failed to skip step');
    }
  };

  const handleEditStep = async (seqId, stepId) => {
    try {
      await axios.put(`${API}/sales/sequences/${seqId}/steps/${stepId}`, {
        subject: editSubject,
        body: editBody
      }, { headers });
      toast.success('Step updated');
      setEditingStep(null);
      const res = await axios.get(`${API}/sales/sequences/${seqId}`, { headers });
      setSelectedSeq(res.data);
    } catch (err) {
      toast.error('Failed to edit step');
    }
  };

  const statusColors = {
    active: 'bg-emerald-500/20 text-emerald-400',
    paused: 'bg-amber-500/20 text-amber-400',
    completed: 'bg-blue-500/20 text-blue-400',
    cancelled: 'bg-red-500/20 text-red-400'
  };

  const stepStatusColors = {
    pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    sent: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    skipped: 'bg-slate-500/20 text-muted-foreground border-slate-500/30'
  };

  if (loading) return <div className="text-muted-foreground text-center py-12">Loading sequences...</div>;

  return (
    <div data-testid="sequence-manager">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {[
          { label: 'Active', value: stats.active || 0, color: 'text-emerald-400' },
          { label: 'Paused', value: stats.paused || 0, color: 'text-amber-400' },
          { label: 'Completed', value: stats.completed || 0, color: 'text-blue-400' },
          { label: 'Emails Sent', value: stats.sent_steps || 0, color: 'text-indigo-400' },
          { label: 'Completion Rate', value: `${stats.completion_rate || 0}%`, color: 'text-foreground' },
        ].map(s => (
          <Card key={s.label} className="bg-muted/60 border-border">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-muted-foreground mb-0.5">{s.label}</p>
              <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pending Approval Banner */}
      {stats.pending_approval > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-between" data-testid="pending-approval-banner">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-400" />
            <span className="text-sm text-amber-400 font-medium">{stats.pending_approval} email{stats.pending_approval > 1 ? 's' : ''} awaiting your approval</span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground">Follow-Up Sequences</h3>
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" onClick={() => setShowCreate(true)} data-testid="new-sequence-btn">
          <Plus className="h-3 w-3 mr-1" /> New Sequence
        </Button>
      </div>

      {selectedSeq ? (
        /* ===== SEQUENCE DETAIL VIEW ===== */
        <div className="space-y-4" data-testid="sequence-detail">
          <Button variant="ghost" size="sm" onClick={() => setSelectedSeq(null)} className="text-xs text-muted-foreground" data-testid="back-to-sequences">
            <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to sequences
          </Button>

          <Card className="bg-muted/60 border-border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base text-foreground">{selectedSeq.lead_name}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">{selectedSeq.lead_email} | {selectedSeq.total_steps} steps | Mode: {selectedSeq.mode}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={statusColors[selectedSeq.status]}>{selectedSeq.status}</Badge>
                  {selectedSeq.status === 'active' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStatusChange(selectedSeq.sequence_id, 'paused')}>
                      <Pause className="h-3 w-3 mr-1" /> Pause
                    </Button>
                  )}
                  {selectedSeq.status === 'paused' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStatusChange(selectedSeq.sequence_id, 'active')}>
                      <Play className="h-3 w-3 mr-1" /> Resume
                    </Button>
                  )}
                  {selectedSeq.status !== 'cancelled' && selectedSeq.status !== 'completed' && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-400" onClick={() => handleStatusChange(selectedSeq.sequence_id, 'cancelled')}>
                      <XCircle className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {selectedSeq.reasoning && (
                <div className="p-2.5 rounded-lg bg-indigo-500/5 border border-indigo-500/10 mb-4">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="h-3 w-3 text-indigo-400" />
                    <span className="text-[10px] font-semibold text-indigo-400">AI Strategy</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{selectedSeq.reasoning}</p>
                </div>
              )}

              {/* Steps Timeline */}
              <div className="space-y-3" data-testid="sequence-steps">
                {(selectedSeq.steps || []).map((step, i) => {
                  const isDue = step.status === 'pending' && new Date(step.scheduled_at) <= new Date();
                  const isEditing = editingStep === step.step_id;
                  return (
                    <div key={step.step_id} className={`relative pl-8 pb-3 ${i < selectedSeq.steps.length - 1 ? 'border-l-2 border-border ml-3' : 'ml-3'}`}>
                      {/* Timeline dot */}
                      <div className={`absolute left-[-7px] top-0 w-3.5 h-3.5 rounded-full border-2 ${
                        step.status === 'sent' ? 'bg-emerald-500 border-emerald-400' :
                        step.status === 'skipped' ? 'bg-slate-600 border-slate-500' :
                        isDue ? 'bg-amber-500 border-amber-400 animate-pulse' :
                        'bg-muted border-border'
                      }`} />

                      <div className={`rounded-lg border p-3 ${isDue ? 'bg-amber-500/5 border-amber-500/20' : 'bg-muted/40 border-border'}`} data-testid={`step-${step.step_id}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-foreground">Step {step.step_number}</span>
                            <Badge className={`text-[9px] ${stepStatusColors[step.status]}`}>{step.status}</Badge>
                            {isDue && selectedSeq.mode === 'approval' && <Badge className="text-[9px] bg-amber-500/30 text-amber-300 animate-pulse">Needs Approval</Badge>}
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            {step.status === 'sent' ? `Sent ${new Date(step.sent_at).toLocaleDateString()}` :
                             `Day +${step.delay_days} | ${new Date(step.scheduled_at).toLocaleDateString()}`}
                          </span>
                        </div>

                        {isEditing ? (
                          <div className="space-y-2 mt-2">
                            <Input value={editSubject} onChange={e => setEditSubject(e.target.value)} className="bg-muted border-border h-8 text-xs" />
                            <Textarea value={editBody} onChange={e => setEditBody(e.target.value)} className="bg-muted border-border text-xs" rows={4} />
                            <div className="flex gap-2">
                              <Button size="sm" className="h-7 text-xs bg-indigo-600" onClick={() => handleEditStep(selectedSeq.sequence_id, step.step_id)}>Save</Button>
                              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingStep(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-xs font-medium text-foreground mb-1">{step.subject}</p>
                            <p className="text-xs text-muted-foreground whitespace-pre-wrap line-clamp-3">{step.body}</p>
                            {step.purpose && <p className="text-[10px] text-muted-foreground mt-1 italic">{step.purpose}</p>}
                          </>
                        )}

                        {step.status === 'pending' && !isEditing && (
                          <div className="flex items-center gap-1.5 mt-2">
                            {isDue && (
                              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveStep(selectedSeq.sequence_id, step.step_id)} data-testid={`approve-${step.step_id}`}>
                                <Send className="h-3 w-3 mr-1" /> Approve & Send
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                              setEditingStep(step.step_id);
                              setEditSubject(step.subject);
                              setEditBody(step.body);
                            }} data-testid={`edit-${step.step_id}`}>
                              <Edit className="h-3 w-3 mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => handleSkipStep(selectedSeq.sequence_id, step.step_id)} data-testid={`skip-${step.step_id}`}>
                              <SkipForward className="h-3 w-3 mr-1" /> Skip
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* ===== SEQUENCE LIST VIEW ===== */
        <div className="space-y-2" data-testid="sequences-list">
          {sequences.map(seq => {
            const completedSteps = (seq.steps || []).filter(s => s.status === 'sent').length;
            const pendingApproval = seq.mode === 'approval' && seq.status === 'active'
              ? (seq.steps || []).filter(s => s.status === 'pending' && new Date(s.scheduled_at) <= new Date()).length : 0;
            return (
              <Card key={seq.sequence_id} className="bg-muted/60 border-border hover:border-border transition-colors cursor-pointer" onClick={() => setSelectedSeq(seq)} data-testid={`seq-${seq.sequence_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Repeat className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                        <p className="text-sm font-semibold text-foreground truncate">{seq.lead_name}</p>
                        <Badge className={`text-[9px] ${statusColors[seq.status]}`}>{seq.status}</Badge>
                        <Badge variant="outline" className="text-[9px] text-muted-foreground">{seq.mode}</Badge>
                        {pendingApproval > 0 && (
                          <Badge className="text-[9px] bg-amber-500/30 text-amber-300 animate-pulse">{pendingApproval} pending</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 ml-6">
                        {completedSteps}/{seq.total_steps} steps sent | Created {new Date(seq.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Mini progress bar */}
                      <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${seq.total_steps > 0 ? (completedSteps / seq.total_steps) * 100 : 0}%` }} />
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {sequences.length === 0 && (
            <div className="text-center py-12">
              <Repeat className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No sequences yet</p>
              <p className="text-muted-foreground text-xs mt-1">Create an AI-powered follow-up sequence to automate outreach</p>
              <Button onClick={() => setShowCreate(true)} className="mt-3 bg-indigo-600 hover:bg-indigo-700" size="sm">
                <Plus className="h-4 w-4 mr-1" /> Create First Sequence
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Create Sequence Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="new-sequence-dialog">
          <DialogHeader><DialogTitle className="text-foreground">Create AI Follow-Up Sequence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-indigo-500/5 border border-indigo-500/10">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4 text-indigo-400" />
                <span className="text-xs font-semibold text-indigo-400">AI-Powered</span>
              </div>
              <p className="text-xs text-muted-foreground">AI will analyze the lead's profile, budget, and engagement history to generate the optimal number of follow-up emails with personalized content and smart timing.</p>
            </div>
            <div>
              <Label className="text-xs">Select Lead *</Label>
              <Select value={newSeq.lead_id} onValueChange={v => setNewSeq(p => ({ ...p, lead_id: v }))}>
                <SelectTrigger className="bg-muted border-border h-9" data-testid="seq-lead-select">
                  <SelectValue placeholder="Choose a lead..." />
                </SelectTrigger>
                <SelectContent>
                  {leads.filter(l => l.status !== 'won' && l.status !== 'lost').map(l => (
                    <SelectItem key={l.lead_id} value={l.lead_id}>
                      {l.client_name} — {l.event_type} (${(l.budget || 0).toLocaleString()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sending Mode</Label>
              <Select value={newSeq.mode} onValueChange={v => setNewSeq(p => ({ ...p, mode: v }))}>
                <SelectTrigger className="bg-muted border-border h-9" data-testid="seq-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approval">Approval Required (review each email before sending)</SelectItem>
                  <SelectItem value="auto">Fully Automatic (sends on schedule, hands-free)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Custom Goal (optional)</Label>
              <Input value={newSeq.custom_goal} onChange={e => setNewSeq(p => ({ ...p, custom_goal: e.target.value }))} className="bg-muted border-border h-9" placeholder="e.g. Get them to book a demo call" data-testid="seq-goal-input" />
            </div>
            <Button onClick={handleCreate} className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={creating || !newSeq.lead_id} data-testid="create-sequence-submit">
              {creating ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> AI is generating...</> : <><Sparkles className="h-4 w-4 mr-1" /> Generate AI Sequence</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ============= PROPOSALS PANEL =============
function ProposalsPanel({ token, navigate }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/proposals`, { headers });
        setProposals(Array.isArray(res.data) ? res.data : []);
      } catch { setProposals([]); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const STATUS_COLORS = {
    draft: 'bg-slate-500/20 text-slate-400',
    sent: 'bg-blue-500/20 text-blue-400',
    viewed: 'bg-amber-500/20 text-amber-400',
    accepted: 'bg-emerald-500/20 text-emerald-400',
    declined: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="space-y-4" data-testid="proposals-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-foreground flex items-center gap-2">
          <FileText className="h-5 w-5 text-indigo-400" /> My Proposals
        </h3>
        <Button size="sm" onClick={() => navigate('/sales/proposals/new')} data-testid="new-proposal-btn">
          <Plus className="h-4 w-4 mr-1" /> New Proposal
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-muted/60"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-foreground">{proposals.length}</p>
          <p className="text-[10px] text-muted-foreground">Total</p>
        </CardContent></Card>
        <Card className="bg-muted/60"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-blue-400">{proposals.filter(p => p.status === 'sent').length}</p>
          <p className="text-[10px] text-muted-foreground">Sent</p>
        </CardContent></Card>
        <Card className="bg-muted/60"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-amber-400">{proposals.filter(p => p.status === 'viewed').length}</p>
          <p className="text-[10px] text-muted-foreground">Viewed</p>
        </CardContent></Card>
        <Card className="bg-muted/60"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-400">{proposals.filter(p => p.status === 'accepted').length}</p>
          <p className="text-[10px] text-muted-foreground">Accepted</p>
        </CardContent></Card>
      </div>

      {/* Proposals List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
      ) : proposals.length === 0 ? (
        <Card className="bg-muted/30">
          <CardContent className="p-8 text-center">
            <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-30" />
            <p className="font-medium text-foreground">No proposals yet</p>
            <p className="text-sm text-muted-foreground mb-4">Create your first proposal from a lead</p>
            <Button onClick={() => navigate('/sales/proposals/new')} data-testid="create-first-proposal">
              <Plus className="h-4 w-4 mr-1" /> Create Proposal
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {proposals.map(p => (
            <Card key={p.proposal_id} className="bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => navigate(`/sales/proposals/${p.proposal_id}`)} data-testid={`proposal-${p.proposal_id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{p.client_name || p.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.event_name || p.event_type || 'Event'} 
                    {p.event_date && ` • ${new Date(p.event_date).toLocaleDateString()}`}
                    {p.guest_count && ` • ${p.guest_count} guests`}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-indigo-400">${(p.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  <Badge className={STATUS_COLORS[p.status] || 'bg-slate-500/20 text-slate-400'}>
                    {p.status?.charAt(0).toUpperCase() + p.status?.slice(1)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ============= CRM PANEL (Fully Inline) =============
function CRMPanel({ token, navigate }) {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [contactType, setContactType] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetail, setContactDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', event_type: '', notes: '' });
  const [creating, setCreating] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const loadContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (contactType && contactType.trim()) params.set('contact_type', contactType.trim());
      params.set('limit', '100');
      const [statsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/crm/stats`, { headers }),
        axios.get(`${API}/crm/contacts?${params}`, { headers }),
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data.contacts || []);
    } catch (err) {
      console.error('CRM load error:', err);
    } finally { setLoading(false); }
  }, [token, search, contactType]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const openContact = async (contact) => {
    setSelectedContact(contact);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/crm/contacts/${contact.contact_id}`, { headers });
      setContactDetail(res.data);
    } catch { toast.error('Failed to load contact'); }
    finally { setDetailLoading(false); }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedContact) return;
    try {
      await axios.post(`${API}/crm/contacts/${selectedContact.contact_id}/notes`, { content: newNote }, { headers });
      setNewNote('');
      openContact(selectedContact);
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  const toggleShare = async (contact) => {
    try {
      await axios.put(`${API}/crm/contacts/${contact.contact_id}/share`, { is_global: !contact.is_global }, { headers });
      toast.success(contact.is_global ? 'Contact set to private' : 'Contact shared globally');
      loadContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to share contact'); }
  };

  const createContact = async () => {
    if (!newContact.name.trim()) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/crm/contacts`, newContact, { headers });
      toast.success('Contact created');
      setShowCreate(false);
      setNewContact({ name: '', email: '', phone: '', event_type: '', notes: '' });
      loadContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create contact'); }
    finally { setCreating(false); }
  };

  const TYPE_COLORS = {
    lead: 'bg-blue-500/20 text-blue-400',
    client: 'bg-emerald-500/20 text-emerald-400',
    vendor: 'bg-purple-500/20 text-purple-400',
  };

  // Contact detail view
  if (selectedContact) {
    return (
      <div className="space-y-4" data-testid="crm-contact-detail">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedContact(null); setContactDetail(null); }} className="text-muted-foreground">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Contacts
        </Button>

        {detailLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
        ) : contactDetail ? (
          <>
            <Card className="bg-muted/60">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center text-lg font-bold text-indigo-400">
                      {contactDetail.contact?.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground">{contactDetail.contact?.name}</h3>
                      <p className="text-sm text-muted-foreground">{contactDetail.contact?.email} {contactDetail.contact?.phone && `• ${contactDetail.contact.phone}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={TYPE_COLORS[contactDetail.contact?.type] || ''}>
                      {contactDetail.contact?.type}
                    </Badge>
                    {contactDetail.contact?.is_global && <Badge className="bg-amber-500/20 text-amber-400">Global</Badge>}
                    {contactDetail.contact?.is_own && (
                      <Button variant="outline" size="sm" onClick={() => toggleShare(contactDetail.contact)} data-testid="toggle-share">
                        <Globe className="h-3.5 w-3.5 mr-1" /> {contactDetail.contact.is_global ? 'Make Private' : 'Share Globally'}
                      </Button>
                    )}
                  </div>
                </div>
                {contactDetail.contact?.event_type && (
                  <div className="mt-3 flex gap-4 text-sm text-muted-foreground">
                    <span>Event: {contactDetail.contact.event_type}</span>
                    {contactDetail.contact.budget > 0 && <span>Budget: ${contactDetail.contact.budget.toLocaleString()}</span>}
                    <span>Status: <Badge variant="outline">{contactDetail.contact.status}</Badge></span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="bg-muted/60">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Notes</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    className="bg-background border-border"
                    onKeyDown={(e) => e.key === 'Enter' && addNote()}
                    data-testid="add-note-input"
                  />
                  <Button size="sm" onClick={addNote} data-testid="add-note-btn">Add</Button>
                </div>
                {(contactDetail.notes || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No notes yet</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {contactDetail.notes.map((note, i) => (
                      <div key={i} className="p-2.5 rounded-lg bg-background/50 text-sm">
                        <p className="text-foreground">{note.content}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{note.author} • {new Date(note.created_at).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Timeline */}
            {contactDetail.timeline?.length > 0 && (
              <Card className="bg-muted/60">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Activity Timeline</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {contactDetail.timeline.slice(0, 10).map((evt, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                        <span className="flex-1">{evt.description}</span>
                        <span>{new Date(evt.timestamp).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="crm-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-foreground flex items-center gap-2">
          <Users className="h-5 w-5 text-indigo-400" /> CRM — My Contacts
        </h3>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="new-contact-btn">
          <Plus className="h-4 w-4 mr-1" /> New Contact
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-muted/60"><CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-foreground">{stats.total_contacts}</p>
            <p className="text-[10px] text-muted-foreground">My Contacts</p>
          </CardContent></Card>
          <Card className="bg-muted/60"><CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-blue-400">{stats.leads}</p>
            <p className="text-[10px] text-muted-foreground">Leads</p>
          </CardContent></Card>
          <Card className="bg-muted/60"><CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-emerald-400">{stats.won_leads}</p>
            <p className="text-[10px] text-muted-foreground">Won</p>
          </CardContent></Card>
          <Card className="bg-muted/60"><CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-amber-400">{stats.new_leads}</p>
            <p className="text-[10px] text-muted-foreground">New</p>
          </CardContent></Card>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search contacts..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-muted/50 border-border" data-testid="crm-search" />
        </div>
        <Select value={contactType} onValueChange={setContactType}>
          <SelectTrigger className="w-32 bg-muted/50 border-border"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value=" ">All</SelectItem>
            <SelectItem value="lead">Leads</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="vendor">Vendors</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Contact List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
      ) : contacts.length === 0 ? (
        <Card className="bg-muted/30"><CardContent className="p-8 text-center text-muted-foreground">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No contacts found</p>
          <p className="text-sm mb-4">Create your first contact to get started</p>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-1" /> New Contact
          </Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-1">
          {contacts.map(c => (
            <div key={c.contact_id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => openContact(c)} data-testid={`contact-${c.contact_id}`}>
              <div className="w-9 h-9 rounded-full bg-muted/50 flex items-center justify-center text-sm font-bold text-muted-foreground flex-shrink-0">
                {c.name?.charAt(0) || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {c.name}
                  {c.is_global && !c.is_own && <span className="text-[10px] text-amber-400 ml-1">(shared)</span>}
                </p>
                <p className="text-[10px] text-muted-foreground truncate">{c.email} {c.phone && `• ${c.phone}`}</p>
              </div>
              <Badge className={`text-[10px] ${TYPE_COLORS[c.type] || 'bg-slate-500/20'}`}>{c.type}</Badge>
              {c.status && <Badge variant="outline" className="text-[10px]">{c.status}</Badge>}
              {c.is_global && <Globe className="h-3 w-3 text-amber-400 flex-shrink-0" />}
            </div>
          ))}
        </div>
      )}

      {/* Create Contact Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="create-contact-dialog">
          <DialogHeader><DialogTitle>New Contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input value={newContact.name} onChange={(e) => setNewContact(p => ({...p, name: e.target.value}))} className="bg-muted border-border h-9" data-testid="contact-name-input" /></div>
            <div><Label className="text-xs">Email</Label><Input value={newContact.email} onChange={(e) => setNewContact(p => ({...p, email: e.target.value}))} className="bg-muted border-border h-9" data-testid="contact-email-input" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={newContact.phone} onChange={(e) => setNewContact(p => ({...p, phone: e.target.value}))} className="bg-muted border-border h-9" data-testid="contact-phone-input" /></div>
            <div><Label className="text-xs">Event Type</Label><Input value={newContact.event_type} onChange={(e) => setNewContact(p => ({...p, event_type: e.target.value}))} placeholder="Wedding, Corporate, etc." className="bg-muted border-border h-9" /></div>
            <div><Label className="text-xs">Notes</Label><Input value={newContact.notes} onChange={(e) => setNewContact(p => ({...p, notes: e.target.value}))} className="bg-muted border-border h-9" /></div>
            <Button onClick={createContact} disabled={creating} className="w-full bg-indigo-600" data-testid="submit-contact">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============= EVENTS PANEL (Confirmed Bookings) =============
function EventsPanel({ token }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        // Fetch won leads as confirmed bookings
        const res = await axios.get(`${API}/sales/leads?status=won`, { headers });
        setEvents(res.data?.leads || []);
      } catch { setEvents([]); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const STATUS_COLORS = {
    confirmed: 'bg-emerald-500/20 text-emerald-400',
    in_progress: 'bg-blue-500/20 text-blue-400',
    completed: 'bg-slate-500/20 text-slate-400',
  };

  return (
    <div className="space-y-4" data-testid="events-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-foreground flex items-center gap-2">
          <Calendar className="h-5 w-5 text-emerald-400" /> Confirmed Events
        </h3>
        <Badge variant="outline">{events.length} events</Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="bg-muted/60"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-400">{events.length}</p>
          <p className="text-[10px] text-muted-foreground">Total Confirmed</p>
        </CardContent></Card>
        <Card className="bg-muted/60"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-blue-400">{events.filter(e => new Date(e.event_date) > new Date()).length}</p>
          <p className="text-[10px] text-muted-foreground">Upcoming</p>
        </CardContent></Card>
        <Card className="bg-muted/60"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-foreground">${events.reduce((s, e) => s + (e.budget || 0), 0).toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">Total Value</p>
        </CardContent></Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
      ) : events.length === 0 ? (
        <Card className="bg-muted/30">
          <CardContent className="p-8 text-center">
            <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-30" />
            <p className="font-medium text-foreground">No confirmed events yet</p>
            <p className="text-sm text-muted-foreground">Won leads will appear here as confirmed bookings</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {events.map(evt => (
            <Card key={evt.lead_id} className="bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`event-${evt.lead_id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-2.5 rounded-lg bg-emerald-500/10 flex-shrink-0">
                  <Calendar className="h-5 w-5 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{evt.client_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {evt.event_type || 'Event'}
                    {evt.event_date && ` • ${new Date(evt.event_date).toLocaleDateString()}`}
                    {evt.guest_count && ` • ${evt.guest_count} guests`}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-emerald-400">${(evt.budget || 0).toLocaleString()}</p>
                  <Badge className={STATUS_COLORS.confirmed}>Confirmed</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ============= MAIN DASHBOARD =============
export default function SalesAgentDashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certStatus, setCertStatus] = useState(null);
  const [certLoading, setCertLoading] = useState(true);
  const [appStatus, setAppStatus] = useState(null);
  const [appLoading, setAppLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('proposals');
  const [showNewLead, setShowNewLead] = useState(false);
  const [showOutreach, setShowOutreach] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [outreachHistory, setOutreachHistory] = useState([]);
  const [scoring, setScoring] = useState(false);

  // Permissions & Stripe Connect
  const [permissions, setPermissions] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  const [newLead, setNewLead] = useState({
    client_name: '', client_email: '', client_phone: '',
    event_type: '', budget: '', guest_count: '', notes: '', source: 'direct'
  });

  const [newOutreach, setNewOutreach] = useState({
    lead_id: '', type: 'call', subject: '', notes: '', duration_minutes: '', outcome: 'positive'
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sales/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchDashboard();
  }, [token, fetchDashboard]);

  // Load permissions
  useEffect(() => {
    const loadPerms = async () => {
      try {
        const res = await axios.get(`${API}/sales/my-permissions`, { headers: { Authorization: `Bearer ${token}` } });
        setPermissions(res.data.permissions);
      } catch { setPermissions(null); }
    };
    if (token) loadPerms();
  }, [token]);

  // Load Stripe Connect status
  useEffect(() => {
    const loadStripe = async () => {
      try {
        const res = await axios.get(`${API}/sales/stripe-connect/status`, { headers: { Authorization: `Bearer ${token}` } });
        setStripeStatus(res.data);
      } catch { setStripeStatus(null); }
    };
    if (token) loadStripe();
  }, [token]);

  const handleStripeConnect = async () => {
    setStripeLoading(true);
    try {
      const res = await axios.post(`${API}/sales/stripe-connect/create-account`, {}, {
        headers: { Authorization: `Bearer ${token}`, origin: window.location.origin }
      });
      if (res.data.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start Stripe onboarding');
    } finally { setStripeLoading(false); }
  };

  const hasPerm = (key) => !permissions || permissions[key] !== false;

  // Certification gate check
  useEffect(() => {
    const checkCert = async () => {
      try {
        const res = await axios.get(`${API}/university/certification-status`, { headers: { Authorization: `Bearer ${token}` } });
        setCertStatus(res.data);
      } catch (err) { console.error('Cert check error:', err); }
      finally { setCertLoading(false); }
    };
    if (token) checkCert();
  }, [token]);

  // Application status check
  useEffect(() => {
    const checkApp = async () => {
      try {
        const res = await axios.get(`${API}/sales/application`, { headers: { Authorization: `Bearer ${token}` } });
        setAppStatus(res.data.status);
      } catch (err) { console.error('App check error:', err); }
      finally { setAppLoading(false); }
    };
    if (token) checkApp();
  }, [token]);

  // Onboarding status check
  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        const res = await axios.get(`${API}/onboarding/status`, { headers: { Authorization: `Bearer ${token}` } });
        setOnboardingStatus(res.data);
      } catch (err) { console.error('Onboarding check error:', err); }
      finally { setOnboardingLoading(false); }
    };
    if (token) checkOnboarding();
  }, [token]);

  const handleCreateLead = async () => {
    try {
      await axios.post(`${API}/sales/leads`, {
        ...newLead,
        budget: parseFloat(newLead.budget) || 0,
        guest_count: parseInt(newLead.guest_count) || 0,
      }, { headers });
      toast.success('Lead created!');
      setShowNewLead(false);
      setNewLead({ client_name: '', client_email: '', client_phone: '', event_type: '', budget: '', guest_count: '', notes: '', source: 'direct' });
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleUpdateLeadStatus = async (leadId, newStatus, budget) => {
    try {
      const body = { status: newStatus };
      if (budget) body.budget = budget;
      await axios.patch(`${API}/sales/leads/${leadId}`, body, { headers });
      toast.success(`Lead updated to ${newStatus}`);
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to update lead');
    }
  };

  const handleLogOutreach = async () => {
    try {
      await axios.post(`${API}/sales/outreach`, {
        ...newOutreach,
        duration_minutes: parseInt(newOutreach.duration_minutes) || null,
      }, { headers });
      toast.success('Activity logged!');
      setShowOutreach(false);
      setNewOutreach({ lead_id: '', type: 'call', subject: '', notes: '', duration_minutes: '', outcome: 'positive' });
      fetchDashboard();
      if (selectedLead) fetchOutreachHistory(selectedLead.lead_id);
    } catch (err) {
      toast.error('Failed to log activity');
    }
  };

  const fetchOutreachHistory = async (leadId) => {
    try {
      const res = await axios.get(`${API}/sales/outreach/${leadId}`, { headers });
      setOutreachHistory(res.data);
    } catch (err) {
      setOutreachHistory([]);
    }
  };

  const openLeadDetail = (lead) => {
    setSelectedLead(lead);
    fetchOutreachHistory(lead.lead_id);
    setActiveTab('lead-detail');
  };

  const handleScoreLeads = async () => {
    setScoring(true);
    try {
      const res = await axios.post(`${API}/sales/ai/score-leads`, {}, { headers });
      toast.success(`Scored ${res.data.total_scored} leads`);
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to score leads');
    } finally {
      setScoring(false);
    }
  };

  if (loading || certLoading || appLoading || onboardingLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
      </div>
    );
  }

  // GATE 1: Must submit and have application approved
  if (appStatus !== 'approved') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="application-gate">
        <div className="max-w-lg mx-auto text-center p-8">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-6">
            <Lock className="h-8 w-8 text-indigo-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {appStatus === 'pending_review' ? 'Application Under Review' :
             appStatus === 'rejected' ? 'Application Not Approved' :
             'Application Required'}
          </h1>
          <p className="text-muted-foreground text-sm mb-6">
            {appStatus === 'pending_review'
              ? 'Your application is being reviewed by our team. You\'ll be able to access the dashboard once approved.'
              : appStatus === 'rejected'
              ? 'Your application was not approved. You may re-apply after 30 days.'
              : 'You need to complete your Sales Agent application before accessing the dashboard.'}
          </p>
          <Button onClick={() => navigate('/sales/apply')} className="bg-indigo-500 hover:bg-indigo-600 w-full" data-testid="go-to-application-btn">
            {appStatus === 'pending_review' ? 'View Application Status' :
             appStatus === 'rejected' ? 'View Application' :
             'Complete Application'}
          </Button>
        </div>
      </div>
    );
  }

  // GATE 2: Must complete onboarding wizard
  if (onboardingStatus && !onboardingStatus.completed && onboardingStatus.status !== 'not_started') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="onboarding-gate">
        <div className="max-w-lg mx-auto text-center p-8">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-6">
            <Rocket className="h-8 w-8 text-indigo-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Complete Your Onboarding</h1>
          <p className="text-muted-foreground text-sm mb-6">
            You're almost there! Complete the onboarding wizard to unlock your Sales Dashboard.
          </p>
          <div className="text-xs text-muted-foreground mb-4">
            Step {onboardingStatus.current_step} of 5
          </div>
          <Button onClick={() => navigate('/sales/onboarding')} className="bg-indigo-500 hover:bg-indigo-600 w-full" data-testid="go-to-onboarding-btn">
            <ArrowRight className="h-4 w-4 mr-2" /> Continue Onboarding
          </Button>
        </div>
      </div>
    );
  }

  // GATE 3: Must complete EU mandatory tracks (fallback for agents who existed before onboarding)
  if (certStatus && !certStatus.certified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="eu-certification-gate">
        <div className="max-w-lg mx-auto text-center p-8">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-6">
            <Lock className="h-8 w-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Eventism University Certification Required</h1>
          <p className="text-muted-foreground text-sm mb-6">
            Complete all mandatory training tracks at Eventism University before accessing the Sales Agent Dashboard.
            Training = Performance = Profits.
          </p>
          <div className="bg-muted/60 border border-border rounded-xl p-4 mb-6 text-left">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Required Tracks</p>
            {certStatus.missing_tracks?.map(t => (
              <div key={t.track_id} className="flex items-center gap-2 py-1.5">
                <Shield className="h-4 w-4 text-amber-400" />
                <span className="text-sm text-muted-foreground">{t.name}</span>
                <Badge className="ml-auto bg-amber-500/20 text-amber-400 text-[10px]">Incomplete</Badge>
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground">
                {certStatus.completed_tracks}/{certStatus.total_mandatory} mandatory tracks completed
              </p>
            </div>
          </div>
          <Button onClick={() => navigate('/university')} className="bg-indigo-500 hover:bg-indigo-600 w-full" data-testid="go-to-university-btn">
            <GraduationCap className="h-4 w-4 mr-2" /> Go to Eventism University
          </Button>
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const leads = dashboard?.leads || [];
  const pipeline = dashboard?.pipeline || {};
  const wallet = dashboard?.wallet || {};
  const transactions = dashboard?.transactions || [];
  const tiers = dashboard?.tiers || [];

  const targetProgress = stats.monthly_target > 0
    ? Math.min((stats.monthly_revenue / stats.monthly_target) * 100, 100) : 0;

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="sales-agent-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground" data-testid="sales-dashboard-title">
              Sales Workspace
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Welcome back, {user?.name}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <CreateEventButton />
            <Button size="sm" variant="outline" onClick={handleScoreLeads} disabled={scoring} className="h-9 text-xs" data-testid="score-leads-btn">
              {scoring ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <BarChart3 className="h-3.5 w-3.5 mr-1 text-amber-400" />}
              AI Score Leads
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/sales/proposals/new')} className="h-9 text-xs" data-testid="new-proposal-quick-btn">
              <PenTool className="h-3.5 w-3.5 mr-1 text-indigo-400" /> New Proposal
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/sales/crm')} className="h-9 text-xs" data-testid="new-contact-quick-btn">
              <Users className="h-3.5 w-3.5 mr-1 text-emerald-400" /> New Contact
            </Button>
            <Button onClick={() => setShowNewLead(true)} className="bg-indigo-600 hover:bg-indigo-700 h-9" data-testid="new-lead-btn">
              <Plus className="h-4 w-4 mr-1" /> New Lead
            </Button>
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="sales-kpi-cards">
          {[
            { icon: Users, label: 'Active Leads', value: stats.active_leads, sub: `${stats.total_leads} total`, color: 'text-muted-foreground', id: 'active-leads' },
            { icon: CheckCircle2, label: 'Won', value: stats.won_leads, sub: `${stats.conversion_rate}% conversion`, color: 'text-emerald-400', id: 'won-leads' },
            { icon: DollarSign, label: 'Revenue', value: `$${stats.total_revenue?.toLocaleString()}`, sub: 'Lifetime closed', color: 'text-amber-400', id: 'total-revenue' },
            { icon: Award, label: 'Tier', value: stats.current_tier, sub: `${(stats.current_rate * 100).toFixed(0)}% rate`, color: 'text-indigo-400', id: 'current-tier' },
            { icon: Wallet, label: 'Balance', value: `$${(wallet.balance || 0).toFixed(2)}`, sub: `$${(wallet.pending_balance || 0).toFixed(2)} pending`, color: 'text-emerald-400', id: 'wallet-balance' },
          ].map(kpi => (
            <Card key={kpi.id} className="bg-muted/60 border-border">
              <CardContent className="p-3">
                <div className={`flex items-center gap-1.5 ${kpi.color} text-[11px] mb-1`}>
                  <kpi.icon className="h-3 w-3" /> {kpi.label}
                </div>
                <p className={`text-xl font-bold ${kpi.color}`} data-testid={kpi.id}>{kpi.value}</p>
                <p className="text-[10px] text-muted-foreground">{kpi.sub}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Monthly Target */}
        {stats.monthly_target > 0 && (
          <Card className="bg-muted/60 border-border mb-6" data-testid="monthly-target">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Target className="h-4 w-4 text-indigo-400" /> Monthly Target
                </span>
                <span className="text-sm font-medium text-foreground">
                  ${stats.monthly_revenue?.toLocaleString()} / ${stats.monthly_target?.toLocaleString()}
                </span>
              </div>
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{
                  width: `${targetProgress}%`,
                  background: targetProgress >= 100 ? 'linear-gradient(90deg, #22c55e, #10b981)' : 'linear-gradient(90deg, #6366f1, #8b5cf6)'
                }} />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{targetProgress.toFixed(0)}% — {stats.monthly_won} deals this month</p>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-card border-border mb-4 flex-wrap h-auto gap-1 p-1">
            {hasPerm('pipeline') && <TabsTrigger value="overview" data-testid="tab-overview">Pipeline</TabsTrigger>}
            {hasPerm('leads') && <TabsTrigger value="leads" data-testid="tab-leads">My Leads</TabsTrigger>}
            {hasPerm('proposals') && (
              <TabsTrigger value="proposals" data-testid="tab-proposals">
                <FileText className="h-3.5 w-3.5 mr-1" /> Proposals
              </TabsTrigger>
            )}
            {hasPerm('crm') && (
              <TabsTrigger value="crm" data-testid="tab-crm">
                <Users className="h-3.5 w-3.5 mr-1" /> CRM
              </TabsTrigger>
            )}
            {hasPerm('ai_copilot') && (
              <TabsTrigger value="copilot" data-testid="tab-copilot">
                <Bot className="h-3.5 w-3.5 mr-1" /> AI Copilot
              </TabsTrigger>
            )}
            {hasPerm('email') && (
              <TabsTrigger value="email" data-testid="tab-email">
                <Mail className="h-3.5 w-3.5 mr-1" /> Email
              </TabsTrigger>
            )}
            {hasPerm('calendar') && (
              <TabsTrigger value="calendar" data-testid="tab-calendar">
                <Calendar className="h-3.5 w-3.5 mr-1" /> Calendar
              </TabsTrigger>
            )}
            {hasPerm('sequences') && (
              <TabsTrigger value="sequences" data-testid="tab-sequences">
                <Repeat className="h-3.5 w-3.5 mr-1" /> Sequences
              </TabsTrigger>
            )}
            {hasPerm('commissions') && (
              <TabsTrigger value="commissions" data-testid="tab-commissions">
                <Wallet className="h-3.5 w-3.5 mr-1" /> Commissions
              </TabsTrigger>
            )}
            {hasPerm('events') && (
              <TabsTrigger value="events" data-testid="tab-events">
                <Calendar className="h-3.5 w-3.5 mr-1" /> Events
              </TabsTrigger>
            )}
            {hasPerm('blog') && (
              <TabsTrigger value="blog" data-testid="tab-blog">
                <BookOpen className="h-3.5 w-3.5 mr-1" /> Blog
              </TabsTrigger>
            )}
            {hasPerm('marketing') && (
              <TabsTrigger value="marketing" data-testid="tab-marketing">
                <TrendingUp className="h-3.5 w-3.5 mr-1" /> Marketing
              </TabsTrigger>
            )}
            {hasPerm('products') && (
              <TabsTrigger value="products" data-testid="tab-products">
                <Layers className="h-3.5 w-3.5 mr-1" /> Products
              </TabsTrigger>
            )}
            {hasPerm('staffing') && (
              <TabsTrigger value="staffing" data-testid="tab-staffing">
                <Building2 className="h-3.5 w-3.5 mr-1" /> Staffing
              </TabsTrigger>
            )}
            {hasPerm('workspaces') && (
              <TabsTrigger value="workspaces" data-testid="tab-workspaces">
                <Layers className="h-3.5 w-3.5 mr-1" /> Workspaces
              </TabsTrigger>
            )}
            {selectedLead && <TabsTrigger value="lead-detail">Lead Detail</TabsTrigger>}
          </TabsList>

          {/* ===== PIPELINE TAB ===== */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="pipeline-board">
              {LEAD_STATUSES.map(s => {
                const p = pipeline[s.value] || { count: 0, value: 0 };
                return (
                  <Card key={s.value} className="bg-muted/60 border-border">
                    <CardContent className="p-3 text-center">
                      <Badge className={`${s.color} text-[10px] mb-2`}>{s.label}</Badge>
                      <p className="text-lg font-bold text-foreground">{p.count}</p>
                      <p className="text-[10px] text-muted-foreground">${p.value?.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Tier Progress */}
            <Card className="bg-muted/60 border-border mb-6" data-testid="tier-progress">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-foreground flex items-center gap-2">
                  <Award className="h-4 w-4 text-amber-400" /> Commission Tiers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  {tiers.map((tier, i) => {
                    const isActive = stats.current_tier === tier.label;
                    const isPast = stats.total_revenue >= tier.threshold;
                    return (
                      <React.Fragment key={tier.label}>
                        {i > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                        <div className={`flex-1 p-3 rounded-lg border text-center ${isActive ? 'bg-indigo-500/20 border-indigo-500/40' : isPast ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-muted/50 border-border'}`}>
                          <p className={`text-xs font-semibold ${isActive ? 'text-indigo-400' : isPast ? 'text-emerald-400' : 'text-muted-foreground'}`}>{tier.label}</p>
                          <p className={`text-sm font-bold ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{(tier.rate * 100).toFixed(0)}%</p>
                          <p className="text-[10px] text-muted-foreground">${tier.threshold.toLocaleString()}+</p>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent leads */}
            <Card className="bg-muted/60 border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-foreground">Recent Leads</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {leads.slice(0, 8).map(lead => {
                    const sDef = LEAD_STATUSES.find(s => s.value === lead.status) || LEAD_STATUSES[0];
                    return (
                      <div key={lead.lead_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/40 hover:bg-muted/60 cursor-pointer transition-colors" onClick={() => openLeadDetail(lead)} data-testid={`lead-${lead.lead_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-foreground truncate">{lead.client_name}</p>
                            {lead.ai_grade && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${lead.ai_grade === 'HOT' ? 'bg-red-500/20 text-red-400' : lead.ai_grade === 'WARM' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                                {lead.ai_score}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground">{lead.event_type} &middot; {lead.guest_count} guests</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-emerald-400">${(lead.budget || 0).toLocaleString()}</span>
                          <Badge className={`${sDef.color} text-[10px]`}>{sDef.label}</Badge>
                        </div>
                      </div>
                    );
                  })}
                  {leads.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No leads yet. Create your first lead!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== LEADS TAB ===== */}
          <TabsContent value="leads">
            <div className="space-y-2" data-testid="leads-list">
              {leads.map(lead => {
                const sDef = LEAD_STATUSES.find(s => s.value === lead.status) || LEAD_STATUSES[0];
                return (
                  <Card key={lead.lead_id} className="bg-muted/60 border-border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openLeadDetail(lead)}>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-foreground">{lead.client_name}</p>
                            <Badge className={`${sDef.color} text-[10px]`}>{sDef.label}</Badge>
                            {lead.ai_grade && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${lead.ai_grade === 'HOT' ? 'bg-red-500/20 text-red-400' : lead.ai_grade === 'WARM' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                                {lead.ai_grade} {lead.ai_score}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {lead.client_email} &middot; {lead.event_type} &middot; {lead.guest_count} guests &middot; ${(lead.budget || 0).toLocaleString()}
                          </p>
                          {lead.notes && <p className="text-xs text-muted-foreground mt-1 truncate max-w-lg">{lead.notes}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Select value={lead.status} onValueChange={(v) => handleUpdateLeadStatus(lead.lead_id, v, lead.budget)}>
                            <SelectTrigger className="w-[130px] h-8 text-xs bg-muted border-border"><SelectValue /></SelectTrigger>
                            <SelectContent>{LEAD_STATUSES.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                          </Select>
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => {
                            setNewOutreach(prev => ({ ...prev, lead_id: lead.lead_id }));
                            setShowOutreach(true);
                          }} data-testid={`log-activity-${lead.lead_id}`}>
                            <Plus className="h-3 w-3 mr-1" /> Activity
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {leads.length === 0 && (
                <div className="text-center py-12">
                  <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No leads yet</p>
                  <Button onClick={() => setShowNewLead(true)} className="mt-3 bg-indigo-600 hover:bg-indigo-700" size="sm">
                    <Plus className="h-4 w-4 mr-1" /> Create First Lead
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ===== AI COPILOT TAB ===== */}
          <TabsContent value="copilot">
            <AICopilot token={token} leads={leads} />
          </TabsContent>

          {/* ===== EMAIL TAB ===== */}
          <TabsContent value="email">
            <EmailWorkspace token={token} leads={leads} />
          </TabsContent>

          {/* ===== CALENDAR TAB ===== */}
          <TabsContent value="calendar">
            <SalesCalendar token={token} leads={leads} />
          </TabsContent>

          {/* ===== SEQUENCES TAB ===== */}
          <TabsContent value="sequences">
            <SequenceManager token={token} leads={leads} />
          </TabsContent>

          {/* ===== COMMISSIONS TAB ===== */}
          <TabsContent value="commissions">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="bg-muted/60 border-border">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">Available Balance</p>
                  <p className="text-2xl font-bold text-emerald-400" data-testid="comm-balance">${(wallet.balance || 0).toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="bg-muted/60 border-border">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">Pending</p>
                  <p className="text-2xl font-bold text-amber-400" data-testid="comm-pending">${(wallet.pending_balance || 0).toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="bg-muted/60 border-border">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">Total Paid Out</p>
                  <p className="text-2xl font-bold text-foreground" data-testid="comm-paid">${(wallet.total_withdrawn || 0).toFixed(2)}</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-muted/60 border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-foreground">Transaction History</CardTitle>
              </CardHeader>
              <CardContent data-testid="commission-table">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground text-xs">Date</TableHead>
                      <TableHead className="text-muted-foreground text-xs">Type</TableHead>
                      <TableHead className="text-muted-foreground text-xs">Description</TableHead>
                      <TableHead className="text-muted-foreground text-xs text-right">Amount</TableHead>
                      <TableHead className="text-muted-foreground text-xs">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map(txn => (
                      <TableRow key={txn.transaction_id} className="border-border/50">
                        <TableCell className="text-xs text-muted-foreground">{new Date(txn.created_at).toLocaleDateString()}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px] capitalize">{txn.type}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{txn.description}</TableCell>
                        <TableCell className={`text-right text-sm font-medium ${txn.type === 'payout' ? 'text-red-400' : 'text-emerald-400'}`}>
                          {txn.type === 'payout' ? '-' : '+'}${txn.amount?.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            txn.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                            txn.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-slate-500/20 text-muted-foreground'
                          }>{txn.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {transactions.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No transactions yet</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Stripe Connect Onboarding */}
            {stripeStatus?.status === 'active' ? (
              <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/20 mt-6" data-testid="stripe-connect-banner">
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-emerald-500/20">
                      <CreditCard className="h-7 w-7 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-emerald-400 mb-0.5">Stripe Connected</h4>
                      <p className="text-sm text-muted-foreground">Payouts are enabled. Commissions will be deposited directly to your bank.</p>
                    </div>
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 ml-auto">Active</Badge>
                  </div>
                </CardContent>
              </Card>
            ) : stripeStatus?.status === 'pending_verification' ? (
              <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20 mt-6" data-testid="stripe-connect-banner">
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="p-3 rounded-xl bg-amber-500/20">
                      <CreditCard className="h-7 w-7 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-amber-400 mb-0.5">Stripe — Pending Verification</h4>
                      <p className="text-sm text-muted-foreground">Stripe is reviewing your account. Payouts will be enabled once verified.</p>
                    </div>
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Pending</Badge>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-indigo-500/20 mt-6" data-testid="stripe-connect-banner">
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="p-3 rounded-xl bg-indigo-500/20">
                      <CreditCard className="h-7 w-7 text-indigo-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-foreground mb-1">Set Up Direct Payouts with Stripe</h4>
                      <p className="text-sm text-muted-foreground">
                        Connect your Stripe account to receive commission payouts directly to your bank. Fast, secure, and automatic.
                      </p>
                    </div>
                    <Button
                      className="bg-indigo-500 hover:bg-indigo-600 flex-shrink-0"
                      onClick={handleStripeConnect}
                      disabled={stripeLoading}
                      data-testid="stripe-connect-btn"
                    >
                      {stripeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                      Connect Stripe
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== PROPOSALS TAB ===== */}
          <TabsContent value="proposals" data-testid="proposals-tab">
            <ProposalsPanel token={token} navigate={navigate} />
          </TabsContent>

          {/* ===== CRM TAB ===== */}
          <TabsContent value="crm" data-testid="crm-tab">
            <CRMPanel token={token} navigate={navigate} />
          </TabsContent>

          {/* ===== EVENTS TAB ===== */}
          <TabsContent value="events" data-testid="events-tab">
            <EventsPanel token={token} />
          </TabsContent>

          {/* ===== BLOG TAB ===== */}
          <TabsContent value="blog" data-testid="blog-tab">
            <div className="text-center py-12" data-testid="blog-panel">
              <BookOpen className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-30" />
              <p className="font-medium text-foreground mb-2">Blog Manager</p>
              <p className="text-sm text-muted-foreground mb-4">Create and manage blog posts to attract leads</p>
              <Button onClick={() => navigate('/admin/blog')} data-testid="open-blog-btn">Open Blog Manager</Button>
            </div>
          </TabsContent>

          {/* ===== MARKETING TAB ===== */}
          <TabsContent value="marketing" data-testid="marketing-tab">
            <div className="text-center py-12" data-testid="marketing-panel">
              <TrendingUp className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-30" />
              <p className="font-medium text-foreground mb-2">AI Marketing Generator</p>
              <p className="text-sm text-muted-foreground mb-4">Generate marketing landing pages with AI</p>
              <Button onClick={() => navigate('/admin/marketing')} data-testid="open-marketing-btn">Open Marketing</Button>
            </div>
          </TabsContent>

          {/* ===== PRODUCTS TAB ===== */}
          <TabsContent value="products" data-testid="products-tab">
            <div className="text-center py-12" data-testid="products-panel">
              <Layers className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-30" />
              <p className="font-medium text-foreground mb-2">Products & Services</p>
              <p className="text-sm text-muted-foreground mb-4">View and manage service catalog</p>
              <Button onClick={() => navigate('/admin/products')} data-testid="open-products-btn">Open Products</Button>
            </div>
          </TabsContent>

          {/* ===== STAFFING TAB ===== */}
          <TabsContent value="staffing" data-testid="staffing-tab">
            <div className="text-center py-12" data-testid="staffing-panel">
              <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-30" />
              <p className="font-medium text-foreground mb-2">Event Staffing</p>
              <p className="text-sm text-muted-foreground mb-4">Manage staffing assignments for events</p>
              <Button onClick={() => navigate('/admin/staffing')} data-testid="open-staffing-btn">Open Staffing</Button>
            </div>
          </TabsContent>

          {/* ===== WORKSPACES TAB ===== */}
          <TabsContent value="workspaces" data-testid="workspaces-tab">
            <div className="text-center py-12" data-testid="workspaces-panel">
              <Layers className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-30" />
              <p className="font-medium text-foreground mb-2">Workspaces</p>
              <p className="text-sm text-muted-foreground mb-4">View active event workspaces</p>
              <Button onClick={() => navigate('/admin/workspaces')} data-testid="open-workspaces-btn">Open Workspaces</Button>
            </div>
          </TabsContent>

          {/* ===== LEAD DETAIL TAB ===== */}
          {selectedLead && (
            <TabsContent value="lead-detail">
              <LeadDetailPanel
                lead={selectedLead}
                token={token}
                onBack={() => setActiveTab('leads')}
                onStatusChange={handleUpdateLeadStatus}
                onLogOutreach={(type) => {
                  setNewOutreach(prev => ({ ...prev, lead_id: selectedLead.lead_id, type: type || 'call' }));
                  setShowOutreach(true);
                }}
                outreachHistory={outreachHistory}
              />
            </TabsContent>
          )}
        </Tabs>

        {/* ===== NEW LEAD DIALOG ===== */}
        <Dialog open={showNewLead} onOpenChange={setShowNewLead}>
          <DialogContent className="bg-card border-border max-w-lg" data-testid="new-lead-dialog">
            <DialogHeader><DialogTitle className="text-foreground">New Lead</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Client Name *</Label><Input value={newLead.client_name} onChange={e => setNewLead(p => ({...p, client_name: e.target.value}))} className="bg-muted border-border h-9" data-testid="lead-name-input" /></div>
                <div><Label className="text-xs">Email *</Label><Input type="email" value={newLead.client_email} onChange={e => setNewLead(p => ({...p, client_email: e.target.value}))} className="bg-muted border-border h-9" data-testid="lead-email-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Phone</Label><Input value={newLead.client_phone} onChange={e => setNewLead(p => ({...p, client_phone: e.target.value}))} className="bg-muted border-border h-9" /></div>
                <div><Label className="text-xs">Event Type</Label><Input value={newLead.event_type} onChange={e => setNewLead(p => ({...p, event_type: e.target.value}))} placeholder="Wedding, Corporate..." className="bg-muted border-border h-9" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Budget ($)</Label><Input type="number" value={newLead.budget} onChange={e => setNewLead(p => ({...p, budget: e.target.value}))} className="bg-muted border-border h-9" data-testid="lead-budget-input" /></div>
                <div><Label className="text-xs">Guest Count</Label><Input type="number" value={newLead.guest_count} onChange={e => setNewLead(p => ({...p, guest_count: e.target.value}))} className="bg-muted border-border h-9" /></div>
              </div>
              <div><Label className="text-xs">Notes</Label><Textarea value={newLead.notes} onChange={e => setNewLead(p => ({...p, notes: e.target.value}))} className="bg-muted border-border" rows={2} /></div>
              <Button onClick={handleCreateLead} className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={!newLead.client_name || !newLead.client_email} data-testid="create-lead-submit">
                <Plus className="h-4 w-4 mr-1" /> Create Lead
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== LOG OUTREACH DIALOG ===== */}
        <Dialog open={showOutreach} onOpenChange={setShowOutreach}>
          <DialogContent className="bg-card border-border max-w-md" data-testid="outreach-dialog">
            <DialogHeader><DialogTitle className="text-foreground">Log Activity</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={newOutreach.type} onValueChange={v => setNewOutreach(p => ({...p, type: v}))}>
                  <SelectTrigger className="bg-muted border-border h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OUTREACH_TYPES.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Subject</Label><Input value={newOutreach.subject} onChange={e => setNewOutreach(p => ({...p, subject: e.target.value}))} className="bg-muted border-border h-9" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Duration (min)</Label><Input type="number" value={newOutreach.duration_minutes} onChange={e => setNewOutreach(p => ({...p, duration_minutes: e.target.value}))} className="bg-muted border-border h-9" /></div>
                <div>
                  <Label className="text-xs">Outcome</Label>
                  <Select value={newOutreach.outcome} onValueChange={v => setNewOutreach(p => ({...p, outcome: v}))}>
                    <SelectTrigger className="bg-muted border-border h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="positive">Positive</SelectItem>
                      <SelectItem value="neutral">Neutral</SelectItem>
                      <SelectItem value="negative">Negative</SelectItem>
                      <SelectItem value="no_answer">No Answer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Notes</Label><Textarea value={newOutreach.notes} onChange={e => setNewOutreach(p => ({...p, notes: e.target.value}))} className="bg-muted border-border" rows={2} /></div>
              <Button onClick={handleLogOutreach} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="log-outreach-submit">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Log Activity
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
